import { getDealFromCache } from '@/features/deals/dealsApi';
import { getClientFromCache } from '@/features/clients/clientsApi';
import { getTaskFromCache } from '@/features/engagements/engagementsApi';
import { relationshipContactsApi } from '@/features/relationshipContacts/relationshipContactsApi';
import { pickPipelineDisplayFieldsFromDealRow } from '@/utils/pipelineDealSync';
import { pickClientDisplayFieldsFromCache } from '@/utils/clientTableSync';
import { pickTaskDisplayFieldsFromCache } from '@/utils/taskTableSync';
import { pickRelationshipContactDisplayFields } from '@/utils/relationshipContactTableSync';
import { mergeRowWithCachedFields } from '@/utils/rowCacheSync';

// Kept separate from rowCacheSync.js: this registry imports the API slices, and the
// *TableSync pickers import rowCacheSync, so combining them risks circular imports.

const DEAL_ROW_CACHE = { getCached: getDealFromCache, pick: pickPipelineDisplayFieldsFromDealRow };

/** Object types whose search rows are overlaid with display fields from RTK Query cache. */
export const ROW_CACHE_BY_OBJECT_TYPE = {
	deal: DEAL_ROW_CACHE,
	debtor: DEAL_ROW_CACHE,
	ticket: DEAL_ROW_CACHE,
	client: { getCached: getClientFromCache, pick: pickClientDisplayFieldsFromCache },
	tasks: { getCached: getTaskFromCache, pick: pickTaskDisplayFieldsFromCache },
	relationshipContact: {
		getCached: (state, id) =>
			relationshipContactsApi.endpoints.getRelationshipContact.select({
				relationshipContactId: id,
			})(state)?.data,
		pick: pickRelationshipContactDisplayFields,
	},
};

/**
 * Serialises the picked cache fields for `rowIds`, for use inside `useSelector` so a
 * component's merged rows recompute whenever one of those cached rows changes.
 * Returns '' for object types with no row cache.
 */
export const buildRowCacheSnapshot = (state, objectType, rowIds) => {
	const rowCache = ROW_CACHE_BY_OBJECT_TYPE[objectType];
	if (!rowCache || rowIds.length === 0) {
		return '';
	}

	return rowIds
		.map((rowId) => {
			const cached = rowCache.getCached(state, rowId);
			if (!cached) {
				return `${rowId}:`;
			}

			return `${rowId}:${JSON.stringify(rowCache.pick(cached))}`;
		})
		.join(';');
};

/** Overlays the cached display fields for `rowId` onto a search row, if any are cached. */
export const mergeCachedRow = (state, objectType, row, rowId) => {
	const rowCache = ROW_CACHE_BY_OBJECT_TYPE[objectType];
	const cached = rowCache?.getCached(state, rowId);
	return cached ? mergeRowWithCachedFields(row, rowCache.pick(cached)) : row;
};
